// Fuente única de cálculo de aspectos (orbes, categorías, longitudes).
// Replica la lógica de matrizAspectosConAngulos para que carta y tablas coincidan.

const SIGNO_INDICE = {
  Aries: 0,
  Tauro: 1,
  'Géminis': 2,
  'Cáncer': 3,
  Leo: 4,
  Virgo: 5,
  Libra: 6,
  Escorpio: 7,
  Sagitario: 8,
  Capricornio: 9,
  Acuario: 10,
  Piscis: 11,
  // nombres en inglés
  Taurus: 1,
  Gemini: 2,
  Cancer: 3,
  Scorpio: 7,
  Sagittarius: 8,
  Capricorn: 9,
  Aquarius: 10,
  Pisces: 11,
};

export const toLongitud = (signo, grado, minutos) => {
  const idx = SIGNO_INDICE[signo] ?? 0;
  return idx * 30 + (grado || 0) + (minutos || 0) / 60;
};

const LUMINARIAS = ['Sol', 'Luna', 'Sun', 'Moon'];
const INTERNOS = [
  'Mercurio',
  'Venus',
  'Marte',
  'Júpiter',
  'Saturno',
  'Mercury',
  'Mars',
  'Jupiter',
  'Saturn',
];
const EXTERNOS = ['Urano', 'Neptuno', 'Plutón', 'Uranus', 'Neptune', 'Pluto'];

export const categoriaAstro = (nombre, ascNombre, mcNombre) => {
  if (nombre === ascNombre || nombre === mcNombre) return 'angulos';
  if (LUMINARIAS.includes(nombre)) return 'luminarias';
  if (INTERNOS.includes(nombre)) return 'internos';
  if (EXTERNOS.includes(nombre)) return 'externos';
  return 'otros';
};

const ORBES_MAYOR_DEFAULT = { luminarias: 8, internos: 7, externos: 6, angulos: 5, otros: 5 };
const ORBES_MENOR_DEFAULT = { luminarias: 2, internos: 1.5, externos: 1, angulos: 3, otros: 0.5 };

const orbe = (cat1, cat2, mayor, userData) => {
  const tbl = mayor ? userData?.mayores || ORBES_MAYOR_DEFAULT : userData?.menores || ORBES_MENOR_DEFAULT;
  const def = mayor ? 5 : 3;
  // Si alguno es ángulo (AC/MC), manda el orbe del ángulo, no el máximo.
  if (cat1 === 'angulos') return tbl[cat1] ?? def;
  if (cat2 === 'angulos') return tbl[cat2] ?? def;
  return Math.max(tbl[cat1] ?? def, tbl[cat2] ?? def);
};

export const ASPECTOS = [
  { deg: 0, key: 'conjuncion', es: 'conjunción', en: 'conjunction', mayor: true },
  { deg: 30, key: 'semisextil', es: 'semisextil', en: 'semi-sextile', mayor: false },
  { deg: 60, key: 'sextil', es: 'sextil', en: 'sextile', mayor: false },
  { deg: 90, key: 'cuadratura', es: 'cuadratura', en: 'square', mayor: true },
  { deg: 120, key: 'trigono', es: 'trígono', en: 'trine', mayor: true },
  { deg: 150, key: 'quincuncio', es: 'quincuncio', en: 'quincunx', mayor: false },
  { deg: 180, key: 'oposicion', es: 'oposición', en: 'opposition', mayor: true },
];

// Devuelve la key del aspecto (ej. 'cuadratura') o null.
export const aspectoEntre = (long1, cat1, long2, cat2, userData) => {
  let dif = Math.abs(long1 - long2);
  dif = Math.min(dif, 360 - dif);
  for (const asp of ASPECTOS) {
    if (Math.abs(dif - asp.deg) <= orbe(cat1, cat2, asp.mayor, userData)) {
      return asp.key;
    }
  }
  return null;
};

// ===========================================================================
// TRÁNSITOS — orbes fijos según la VELOCIDAD del planeta transitante.
// (Fundación Centro Astrológico de Buenos Aires — "Orbes Tránsitos a Natal".)
// El orbe lo define SOLO el planeta que transita; el factor natal no influye.
// El corte de velocidad cae entre Marte y Júpiter (no entre Saturno y Urano),
// por eso personales = Me/Ve/Ma y transpersonales = Júpiter→Plutón.
// ===========================================================================

const TRANSITO_LUMINARIAS = ['Sol', 'Luna', 'Sun', 'Moon'];
const TRANSITO_PERSONALES = ['Mercurio', 'Venus', 'Marte', 'Mercury', 'Mars'];
const TRANSITO_TRANSPERSONALES = [
  'Júpiter',
  'Saturno',
  'Urano',
  'Neptuno',
  'Plutón',
  'Jupiter',
  'Saturn',
  'Uranus',
  'Neptune',
  'Pluto',
];

export const categoriaTransito = (nombre) => {
  if (TRANSITO_LUMINARIAS.includes(nombre)) return 'luminarias';
  if (TRANSITO_PERSONALES.includes(nombre)) return 'personales';
  if (TRANSITO_TRANSPERSONALES.includes(nombre)) return 'transpersonales';
  return 'otros';
};

const ORBES_TRANSITO = {
  luminarias: { mayor: 8, menor: 4 },
  personales: { mayor: 6, menor: 3 },
  transpersonales: { mayor: 4, menor: 2 },
  otros: { mayor: 4, menor: 2 },
};
// Mercurio/Venus/Marte cerca de retrogradación/estacionamiento (lentos).
const ORBE_PERSONAL_LENTO = { mayor: 2, menor: 2 };

// Devuelve la key del aspecto de tránsito o null.
// nombreTransitante: planeta que transita; retro: si está retrógrado (proxy de "lento").
export const aspectoTransito = (long1, long2, nombreTransitante, retro) => {
  let dif = Math.abs(long1 - long2);
  dif = Math.min(dif, 360 - dif);
  const cat = categoriaTransito(nombreTransitante);
  const tabla = cat === 'personales' && retro ? ORBE_PERSONAL_LENTO : ORBES_TRANSITO[cat];
  for (const asp of ASPECTOS) {
    const orb = asp.mayor ? tabla.mayor : tabla.menor;
    if (Math.abs(dif - asp.deg) <= orb) return asp.key;
  }
  return null;
};
